import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "../branding/Logo";
import { DisclaimerBanner } from "./DisclaimerBanner";
import { Sparkles, ShieldCheck, Camera, HeartPulse } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1663637875268-7bba6bc372fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxjYXQlMjBwb3J0cmFpdCUyMHdhcm0lMjBsaWdodGluZ3xlbnwwfHx8fDE3ODUzMjgyMTV8MA&ixlib=rb-4.1.0&q=85";

export function StepIntro({ onNext }) {
    return (
        <div className="pb-24" data-testid="step-intro">
            <div className="relative -mx-5 -mt-2 h-[280px] overflow-hidden">
                <img
                    src={HERO_IMG}
                    alt="A calm cat resting in warm light"
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-meow-cream" />
                <div className="absolute inset-x-0 bottom-0 px-5 pb-4">
                    <Logo variant="hero" />
                </div>
            </div>

            <div className="mt-6 space-y-4">
                <h1 className="font-display text-[2.15rem] font-medium leading-[1.05] text-meow-charcoal">
                    A friendly second look at your cat&apos;s health.
                </h1>
                <p className="text-[15px] leading-relaxed text-meow-muted">
                    Two short phases, an optional photo check, and a personalised
                    8-category report — all in about five minutes. Free.
                </p>

                <div className="mt-2 space-y-2.5">
                    <Feature
                        icon={<HeartPulse className="h-4 w-4" />}
                        title="Baseline + personalised follow-ups"
                        body="7 daily-life questions, then 10 AI-tailored ones."
                    />
                    <Feature
                        icon={<Camera className="h-4 w-4" />}
                        title="Optional photo check"
                        body="Claude Vision flags visible signs. Skip anytime."
                    />
                    <Feature
                        icon={<Sparkles className="h-4 w-4" />}
                        title="Full written report"
                        body="Score, 8-category breakdown, diet plan, next steps."
                    />
                    <Feature
                        icon={<ShieldCheck className="h-4 w-4" />}
                        title="Educational, not veterinary advice"
                        body={
                            <>
                                Read the{" "}
                                <Link
                                    to="/disclaimer"
                                    className="font-medium text-meow-teal underline-offset-2 hover:underline"
                                    data-testid="intro-disclaimer-link"
                                >
                                    full medical disclaimer
                                </Link>
                                .
                            </>
                        }
                    />
                </div>

                <DisclaimerBanner />
            </div>

            <StickyCta>
                <button
                    type="button"
                    className="meow-btn-primary w-full"
                    onClick={onNext}
                    data-testid="intro-start-btn"
                >
                    Start the free check
                </button>
            </StickyCta>
        </div>
    );
}

function Feature({ icon, title, body }) {
    return (
        <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-meow-icon text-meow-teal">
                {icon}
            </div>
            <div className="flex-1">
                <div className="font-display text-[15px] font-medium text-meow-charcoal">
                    {title}
                </div>
                <div className="text-[13px] leading-relaxed text-meow-muted">{body}</div>
            </div>
        </div>
    );
}

export function StickyCta({ children }) {
    return (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30">
            <div className="mx-auto max-w-[440px] px-5 pb-5 pt-8 bg-gradient-to-t from-meow-cream via-meow-cream/95 to-transparent">
                <div className="pointer-events-auto">{children}</div>
            </div>
        </div>
    );
}
